import { useEffect, useState, useMemo, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, LogOut, Download, Search, RefreshCw, TrendingUp, Users, Banknote, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

function toCSV(rows, columns) {
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const header = columns.map((c) => escape(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => escape(r[c.key])).join(",")).join("\n");
  return `${header}\n${body}`;
}

function download(name, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

export default function AdminDashboard() {
  const { user, loading, logout, authHeader } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [finance, setFinance] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const [e, f, s] = await Promise.all([
        api.get("/admin/enquiries", { headers: authHeader() }),
        api.get("/admin/finance-applications", { headers: authHeader() }),
        api.get("/admin/stats", { headers: authHeader() }),
      ]);
      setEnquiries(e.data || []);
      setFinance(f.data || []);
      setStats(s.data || null);
    } catch (err) {
      toast.error("Could not load admin data");
    } finally {
      setBusy(false);
    }
  }, [authHeader]);

  useEffect(() => { if (user) refresh(); }, [user, refresh]);

  const filteredEnq = useMemo(() => {
    if (!q) return enquiries;
    const t = q.toLowerCase();
    return enquiries.filter((e) =>
      [e.name, e.phone, e.email, e.city, e.vehicle_interest, e.message].some((v) =>
        (v || "").toString().toLowerCase().includes(t)
      )
    );
  }, [enquiries, q]);

  const filteredFin = useMemo(() => {
    if (!q) return finance;
    const t = q.toLowerCase();
    return finance.filter((f) =>
      [f.name, f.phone, f.email, f.vehicle_interest].some((v) =>
        (v || "").toString().toLowerCase().includes(t)
      )
    );
  }, [finance, q]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const exportEnq = () => download(
    `enquiries-${new Date().toISOString().slice(0,10)}.csv`,
    toCSV(filteredEnq, [
      { key: "created_at", label: "Received" },
      { key: "name", label: "Name" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "city", label: "City" },
      { key: "vehicle_interest", label: "Vehicle" },
      { key: "source", label: "Source" },
      { key: "message", label: "Message" },
    ])
  );

  const exportFin = () => download(
    `finance-${new Date().toISOString().slice(0,10)}.csv`,
    toCSV(filteredFin, [
      { key: "created_at", label: "Received" },
      { key: "name", label: "Name" },
      { key: "phone", label: "Phone" },
      { key: "vehicle_interest", label: "Vehicle" },
      { key: "on_road_price", label: "On-Road Price" },
      { key: "down_payment", label: "Down Payment" },
      { key: "tenure_months", label: "Tenure (mo)" },
    ])
  );

  return (
    <div className="min-h-screen" data-testid="admin-dashboard-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-red-500 font-mono-brand mb-2">Admin Panel</div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-white">
              Leads <span className="text-gradient-red">Dashboard</span>
            </h1>
            <p className="text-slate-400 text-sm mt-2">Signed in as <span className="text-white font-medium">{user.email}</span></p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={refresh}
              disabled={busy}
              variant="outline"
              className="border-white/15 bg-white/5 text-white hover:bg-white/10 rounded-full"
              data-testid="admin-refresh-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh
            </Button>
            <Button
              onClick={logout}
              variant="outline"
              className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 rounded-full"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <StatCard icon={Users} label="Total Enquiries" value={stats.total_enquiries} accent="red" />
            <StatCard icon={Banknote} label="Finance Applications" value={stats.total_finance} accent="blue" />
            <StatCard icon={CalendarDays} label="Enquiries (7d)" value={stats.enquiries_7d} accent="red" />
            <StatCard icon={TrendingUp} label="Finance (7d)" value={stats.finance_7d} accent="blue" />
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, phone, vehicle…"
            className="pl-11 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500 rounded-full h-11"
            data-testid="admin-search-input"
          />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="enquiries" className="w-full">
          <TabsList className="bg-white/5 border border-white/10 rounded-full p-1 mb-6">
            <TabsTrigger value="enquiries" className="rounded-full data-[state=active]:bg-red-600 data-[state=active]:text-white text-slate-300" data-testid="tab-enquiries">
              Enquiries ({filteredEnq.length})
            </TabsTrigger>
            <TabsTrigger value="finance" className="rounded-full data-[state=active]:bg-red-600 data-[state=active]:text-white text-slate-300" data-testid="tab-finance">
              Finance ({filteredFin.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="enquiries" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={exportEnq} className="bg-red-600 hover:bg-red-700 rounded-full" data-testid="admin-export-enquiries">
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </div>
            <LeadTable rows={filteredEnq} type="enquiry" />
          </TabsContent>

          <TabsContent value="finance" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={exportFin} className="bg-red-600 hover:bg-red-700 rounded-full" data-testid="admin-export-finance">
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </div>
            <LeadTable rows={filteredFin} type="finance" />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  const glow = accent === "red" ? "radial-glow-red" : "radial-glow-blue";
  return (
    <div className="relative overflow-hidden rounded-2xl glass p-5">
      <div className={`absolute -top-16 -right-16 w-40 h-40 ${glow} pointer-events-none`} />
      <div className="relative">
        <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-3">
          <Icon className="w-4 h-4 text-white" />
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
        <div className="font-display font-black text-3xl text-white mt-1">{value}</div>
      </div>
    </div>
  );
}

function LeadTable({ rows, type }) {
  if (!rows.length) {
    return <div className="glass rounded-2xl p-10 text-center text-slate-400">No records yet.</div>;
  }
  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white/5 text-slate-400 uppercase text-[10px] tracking-widest">
              <th className="text-left px-4 py-3">Received</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Phone</th>
              <th className="text-left px-4 py-3">Vehicle</th>
              {type === "enquiry" ? (
                <>
                  <th className="text-left px-4 py-3">City</th>
                  <th className="text-left px-4 py-3">Message</th>
                </>
              ) : (
                <>
                  <th className="text-right px-4 py-3">Price</th>
                  <th className="text-right px-4 py-3">Down</th>
                  <th className="text-right px-4 py-3">Tenure</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i} className="border-t border-white/5 hover:bg-white/[0.03] transition-colors">
                <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3 text-white font-medium">{r.name}</td>
                <td className="px-4 py-3 text-slate-300">
                  <a href={`tel:${r.phone}`} className="hover:text-red-400">{r.phone}</a>
                </td>
                <td className="px-4 py-3 text-slate-300">{r.vehicle_interest || "—"}</td>
                {type === "enquiry" ? (
                  <>
                    <td className="px-4 py-3 text-slate-300">{r.city || "—"}</td>
                    <td className="px-4 py-3 text-slate-400 max-w-xs truncate">{r.message || "—"}</td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3 text-slate-300 text-right">₹{Number(r.on_road_price || 0).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-slate-300 text-right">₹{Number(r.down_payment || 0).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-slate-300 text-right">{r.tenure_months} mo</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
